import React, { useState, useEffect, useCallback } from 'react'
import { getSession } from '../../getSession'
import { getMailSmsDLTAccount, getSenderIdDLT, getDLTTemplates, sendMassSmsNew } from '../../utils/lead'
import Swal from 'sweetalert2'
import { wcfInstance } from '../../axiosinstance'

const SendSMS = ({
  onClose = () => {},
  onSend = (data) => {},
  initialRecipients = { mobile1: true, mobile2: false, mobile3: false },
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  // optional prop overrides (parent can pass dynamic arrays)
  senderOptions: propSenderOptions,
  accountOptions: propAccountOptions,
  templateOptions: propTemplateOptions,
  registerSubmitHandler,
}) => {
  const [recipients, setRecipients] = useState(initialRecipients)
  const [sender, setSender] = useState('')
  const [account, setAccount] = useState('')
  const [template, setTemplate] = useState('')
  const [message, setMessage] = useState('')
  const [isUnicode, setIsUnicode] = useState(false)
  const [trackUrl, setTrackUrl] = useState(false)
  const [error, setError] = useState('')

  const [senderOptions, setSenderOptions] = useState(propSenderOptions || [])
  const [accountOptions, setAccountOptions] = useState(propAccountOptions || [])
  const [templateOptions, setTemplateOptions] = useState(propTemplateOptions || [])

  const extractTemplateText = (templateItem) => {
    if (!templateItem || typeof templateItem !== 'object') return ''
    return (
      templateItem.templateText
      ?? templateItem.TemplateText
      ?? templateItem.Message
      ?? templateItem.message
      ?? templateItem.Body
      ?? templateItem.body
      ?? ''
    )
  }

  const extractTemplateUnicode = (templateItem) => {
    if (!templateItem || typeof templateItem !== 'object') return null
    const rawValue = templateItem.isUnicode ?? templateItem.IsUnicode
    if (rawValue === undefined || rawValue === null) return null
    if (typeof rawValue === 'boolean') return rawValue
    return String(rawValue).toLowerCase() === 'true'
  }

  const fetchTemplateText = async (templateId) => {
    try {
      const { userId } = getSession()
      const response = await wcfInstance.post(
        'UserCRMCampaign/Service/CRMService.asmx/Get_DLTTemplateText',
        { DLTTemplateID: templateId, UserId: Number(userId) || userId }
      )

      const data = response?.data?.d ?? response?.data ?? []
      const templateDetail = Array.isArray(data) ? data[0] : data
      if (!templateDetail) return

      const templateText = templateDetail.TemplateText ?? templateDetail.Message ?? ''
      if (templateText) {
        setMessage(templateText)
      }

      const unicodeFlag = extractTemplateUnicode(templateDetail)
      if (unicodeFlag !== null) {
        setIsUnicode(unicodeFlag)
      }
    } catch (error) {
      console.warn('Failed to fetch template text:', error?.message || error)
    }
  }

  useEffect(() => {
    if (propSenderOptions && propSenderOptions.length) {
      setSenderOptions(propSenderOptions);
      if (!sender) setSender(propSenderOptions[0]);
    }
  }, [propSenderOptions]);

  useEffect(() => {
    if (propAccountOptions && propAccountOptions.length) {
      setAccountOptions(propAccountOptions);
      if (!account) setAccount(propAccountOptions[0]);
    }
  }, [propAccountOptions]);

  useEffect(() => {
    if (propTemplateOptions && propTemplateOptions.length) {
      setTemplateOptions(propTemplateOptions);
      if (!template) setTemplate(propTemplateOptions[0].value ?? propTemplateOptions[0].id ?? '');
    }
  }, [propTemplateOptions]);

  useEffect(() => {
    if (!template) return

    const selectedTemplate = templateOptions.find((item) => {
      if (!item || typeof item !== 'object') return false
      return String(item.value ?? item.id ?? '') === String(template)
    })

    const inlineTemplateText = extractTemplateText(selectedTemplate)
    if (inlineTemplateText) {
      setMessage(inlineTemplateText)
      const unicodeFlag = extractTemplateUnicode(selectedTemplate)
      if (unicodeFlag !== null) {
        setIsUnicode(unicodeFlag)
      }
      return
    }

    fetchTemplateText(template)
  }, [template, templateOptions])

  // Fetch DLT accounts (for Account dropdown)
  useEffect(() => {
    let mounted = true
    const fetchAccounts = async () => {
      try {
        const { userId, TokenId, parentId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { ParentId: parentId, IsSms: true },
          BroadcastName: ''
        };
        const resp = await getMailSmsDLTAccount(payload);
        let list = resp?.Details ?? resp?.d ?? resp ?? [];
        if (list && list.data) list = list.data;
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Accounts ?? list;
        }
        const accountsArray = Array.isArray(list) ? list.map(item => {
          if (!item) return '';
          if (typeof item === 'string') return item;
          return item.AccountName || item.Name || item.Value || item.Text || item.DisplayName || JSON.stringify(item);
        }).filter(Boolean) : [];
        if (mounted && accountsArray.length && !propAccountOptions) {
          setAccountOptions(['--Select Accounts--', ...accountsArray]);
          setAccount(accountsArray[0]);
        }
      } catch (err) {
        console.warn('Failed to load DLT accounts:', err?.message || err);
      }
    }
    fetchAccounts();
    return () => { mounted = false }
  }, [])

  // Fetch sender ids (for Sender dropdown)
  useEffect(() => {
    let mounted = true
    const fetchSenderIds = async () => {
      try {
        const { userId, TokenId } = getSession();
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { AccUserId: userId },
          BroadcastName: ''
        };
        const resp = await getSenderIdDLT(payload);
        let list = resp ?? [];
        if (list && list.data) list = list.data;
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Accounts ?? list.Details ?? list;
        }
        const sendersArray = Array.isArray(list) ? list.map(item => {
          if (!item) return '';
          if (typeof item === 'string') return item;
          const code = item.Code ?? item.Sender_ID ?? item.SenderId ?? '';
          const text = item.Text ?? item.Sender_Name ?? item.SenderName ?? '';
          return text ? `${text}${code ? ' (' + code + ')' : ''}` : (code || JSON.stringify(item));
        }).filter(Boolean) : [];
        if (mounted && sendersArray.length && !propSenderOptions) {
          setSenderOptions(['--Select App--', ...sendersArray]);
          setSender(sendersArray[0]);
        }
      } catch (err) {
        console.warn('Failed to load SenderIdDLT:', err?.message || err);
      }
    }
    fetchSenderIds();
    return () => { mounted = false }
  }, [])

  // Fetch templates when sender changes
  useEffect(() => {
    let mounted = true
    const fetchTemplates = async () => {
      try {
        if (!sender) return;
        const { userId, TokenId } = getSession();
        let senderCode = '';
        if (sender && typeof sender === 'string') {
          const m = sender.match(/^(.*)\s*\(([^)]+)\)\s*$/);
          senderCode = m ? m[1].trim() : (sender === '--Select App--' || sender === '' ? '' : sender);
        }
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { SenderID: senderCode, UserId: userId, isenquiry: false, islead: true },
          BroadcastName: ''
        };
        const resp = await getDLTTemplates(payload);
        let list = resp ?? [];
        if (list && list.data) list = list.data;
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Templates ?? list;
        }
        const tpl = Array.isArray(list) ? list.map(item => {
          if (!item) return null;
          if (typeof item === 'string') return { value: item, label: item };
          const value = item.Code ?? item.DLT_TemplateID ?? item.TemplateId ?? '';
          const label = item.Text ?? item.TemplateName ?? item.Text ?? JSON.stringify(item);
          return {
            value: value || label,
            label,
            templateText: item.TemplateText ?? item.Message ?? item.Body ?? '',
            isUnicode: item.IsUnicode
          };
        }).filter(Boolean) : [];
        if (mounted && tpl.length && !propTemplateOptions) {
          setTemplateOptions(tpl);
          setTemplate(tpl[0].value);
        }
      } catch (err) {
        console.warn('Failed to load DLT templates:', err?.message || err);
      }
    }
    fetchTemplates();
    return () => { mounted = false }
  }, [sender])

  const toggleRecipient = (key) => {
    setRecipients(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const extractAccountId = (accountValue) => {
    if (!accountValue) return 0
    if (typeof accountValue === 'number' && Number.isFinite(accountValue)) return accountValue

    const normalized = String(accountValue).trim()
    if (!normalized) return 0

    const directNumeric = Number(normalized)
    if (Number.isFinite(directNumeric) && directNumeric > 0) return directNumeric

    const allNumberParts = normalized.match(/\d+/g)
    if (!allNumberParts || !allNumberParts.length) return 0

    const parsed = Number(allNumberParts[allNumberParts.length - 1])
    return Number.isFinite(parsed) ? parsed : 0
  }

  const handleSend = useCallback(async () => {
    try {
      setError('')
      if (!Object.values(recipients).some(Boolean)) {
        setError('Select at least one recipient number')
        return
      }
      if (!message.trim() && !template) {
        setError('Message cannot be empty')
        return
      }

      const { userId, TokenId } = getSession()
      const numericUserId = Number(userId) || 0
      const numericBatchId = Number(massOpBatchId) || 0
      const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.join('/') : '') || '').trim()
      const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
      const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

      if (!shouldUseBatchId && !idsForPayload) {
        setError('No leads selected')
        return
      }

      let senderId = ''
      if (sender && typeof sender === 'string') {
        const m = sender.match(/^(.*)\s*\(([^)]+)\)\s*$/)
        if (m) {
          senderId = m[2].trim()
        } else {
          senderId = sender
        }
      }

      const accountId = extractAccountId(account)

      const payload = {
        Token: TokenId,
        Message: '',
        LoggedUserId: numericUserId,
        MAC_Address: '',
        IP_Address: '',
        Details: {
          massLeadSmses: [
            {
              IDS: idsForPayload,
              AccountID: accountId,
              SenderID: senderId,
              Message: message || '',
              IsUniCode: !!isUnicode,
              IsMobile: !!recipients.mobile1,
              IsMobile1: !!recipients.mobile2,
              IsMobile2: !!recipients.mobile3,
              TemplateID: template || '',
              sms_source: 'lead',
              smsDateTime: '',
              Chksmsurl: !!trackUrl
            }
          ],
          IDS: idsForPayload,
          Userid: numericUserId,
          massbatchid: shouldUseBatchId ? numericBatchId : 0,
          Module: 'Lead',
          ModuleType: 'lead',
          StartDateTime: ''
        },
        BroadcastName: ''
      }

      const resp = await sendMassSmsNew(payload)
      const status = Number(resp?.Status ?? resp?.status ?? 0)
      const details = resp?.Details
      const detailMessage = typeof details === 'string'
        ? details
        : (Array.isArray(details)
          ? (details[0]?.Result || details[0]?.Message || '')
          : (details?.Result || details?.Message || ''))
      const blockedByBackend = /insufficient balance|not matching with template text/i.test(String(detailMessage || ''))

      if (status === 1 && !blockedByBackend) {
        Swal.fire({ icon: 'success', title: 'Sent', text: 'SMS sent successfully.' })
        onClose()
      } else {
        const failureMessage = detailMessage || resp?.Message || 'Failed to send SMS.'
        Swal.fire({ icon: 'error', title: 'Error', text: failureMessage })
      }
    } catch (err) {
      console.error('send mass sms error', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while sending SMS.' })
    }
  }, [
    recipients,
    message,
    template,
    massOpBatchId,
    hashedIds,
    selectedLeadIds,
    applyOnAllLeads,
    sender,
    account,
    isUnicode,
    trackUrl,
    onClose
  ])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => handleSend)
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler, handleSend])

  return (
    <div className=" w-full max-w-2xl">
      {selectedCount >1 && (<div className="mb-3 text-sm text-blue-700 bg-blue-50 p-3 rounded">Info! You are about to perform a mass action on {selectedCount} record(s)</div>)}

      <div className="mb-4">
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={recipients.mobile1} onChange={() => toggleRecipient('mobile1')} className=" rounded-[3px] mr-2" />
          Mobile1
        </label>
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={recipients.mobile2} onChange={() => toggleRecipient('mobile2')} className=" rounded-[3px] mr-2" />
          Mobile2
        </label>
        <label className="inline-flex items-center">
          <input type="checkbox" checked={recipients.mobile3} onChange={() => toggleRecipient('mobile3')} className=" rounded-[3px] mr-2" />
          Mobile3
        </label>
      </div>

      <div className="space-y-3">
        <div>
          <select value={sender} onChange={e => setSender(e.target.value)} className="w-full border px-3 py-2 rounded">
            {senderOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <select value={account} onChange={e => setAccount(e.target.value)} className="w-full border px-3 py-2 rounded">
            {accountOptions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        <div>
          <select value={template} onChange={e => setTemplate(e.target.value)} className="w-full border px-3 py-2 rounded">
            {templateOptions.map(t => <option key={t.value ?? t.id ?? JSON.stringify(t)} value={t.value ?? t.id ?? ''}>{t.label ?? t.label ?? (t.value ?? t.id)}</option>)}
          </select>
        </div>

        <div>
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Message" className="w-full border px-3 py-2 rounded h-28 resize-none" />
        </div>

        <div className="flex items-center gap-6">
          <label className="inline-flex items-center">
            <input type="checkbox" checked={isUnicode} onChange={() => setIsUnicode(v => !v)} className=" rounded-[3px] mr-2" />
            IsUnicode
          </label>

          <label className="inline-flex items-center">
            <input type="checkbox" checked={trackUrl} onChange={() => setTrackUrl(v => !v)} className=" rounded-[3px] mr-2" />
            Track Url
          </label>
        </div>

        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={handleSend} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">Track SMS</button>
        </div>
      </div>
    </div>
  )
}

export default SendSMS
